import {
  differenceInCalendarDays,
  format as dateFormat,
  formatDistanceStrict,
  isValid,
  Locale,
  parseISO,
} from 'date-fns'
import { enCA, enUS, es, frCA } from 'date-fns/locale'
import { TFunction } from 'i18next'

import logger from '@cash-web/shared/util-logger'

const FALLBACK_TAG = 'en-US'

// Presentation is keyed on the BCP-47 tag and left to Intl, because clock and field order are
// region data, not language data: es-US is a 12-hour locale while es-ES is 24-hour, and a
// language-only locale object cannot carry that difference.
//
// These are named styles rather than pattern strings because a pattern hardcodes field order and
// connector words, which are also locale data. `MMMM d` renders "septiembre 15" in es-US no matter
// which date-fns Locale it is given, and `MMMM yyyy` renders "septiembre 2026" without the "de"
// that Spanish requires. Only CLDR knows the shape, so callers name the fields they want and Intl
// arranges them.
const PRESENTATION_FORMATS = {
  weekday: { weekday: 'long' },
  weekdayMonthDay: { weekday: 'short', month: 'short', day: 'numeric' },
  weekdayShortTime: { weekday: 'short', hour: 'numeric', minute: '2-digit' },
  monthDay: { month: 'short', day: 'numeric' },
  monthDayHour: { month: 'short', day: 'numeric', hour: 'numeric' },
  monthDayLong: { month: 'long', day: 'numeric' },
  monthDayYear: { month: 'short', day: 'numeric', year: 'numeric' },
  monthDayYearLong: { month: 'long', day: 'numeric', year: 'numeric' },
  monthYear: { month: 'long', year: 'numeric' },
  time: { hour: 'numeric', minute: '2-digit' },
} satisfies Record<string, Intl.DateTimeFormatOptions>

export type DatePresentation = keyof typeof PRESENTATION_FORMATS

// date-fns still backs `parseISODate` and `format`, whose callers supply date-fns patterns. Those
// need a Locale object per locale and date-fns ships ~200 of them, so the set stays an explicit
// import list to keep the rest out of a bundle 35+ projects pull in.
const DATE_FNS_LOCALES: Locale[] = [enUS, enCA, es, frCA]

const languageOf = (tag: string) => tag.toLowerCase().split('-')[0]

// `date()` re-resolves on every render, so report each distinct problem once.
const reported = new Set<string>()

const reportOnce = (message: string) => {
  if (reported.has(message)) {
    return
  }
  reported.add(message)
  logger.warn(new Error(message))
}

// Intl throws a RangeError on a malformed tag (including `''` and underscore forms like `en_US`),
// and returns an empty array for a well-formed tag it has no data for. Empty has to count as a
// miss: `new Intl.DateTimeFormat` resolves such a tag through CLDR's own fallback chain, and that
// chain terminates at the runtime default locale -- the visitor's browser -- not at en-US.
const localeDataProblem = (tag: string): string | undefined => {
  try {
    if (Intl.DateTimeFormat.supportedLocalesOf(tag).length === 0) {
      return `No locale data for the tag ${JSON.stringify(tag)}`
    }
  } catch {
    return `Malformed locale tag ${JSON.stringify(tag)}`
  }
  return undefined
}

const resolveTag = (tag: string): string => {
  const problem = localeDataProblem(tag)

  if (!problem) {
    return tag
  }
  reportOnce(`${problem}, formatting dates as "${FALLBACK_TAG}"`)
  return FALLBACK_TAG
}

// A tag Intl cannot render is reported and rendered in en-US; a missing `window.locale` is not,
// since en-US is the app's documented default rather than a degraded render.
export function getLocaleTag(): string {
  if (typeof window === 'undefined' || !('locale' in window)) {
    return FALLBACK_TAG
  }
  return resolveTag((window.locale as string) ?? '')
}

// date-fns carries locale data for far fewer tags than Intl, so `parseISODate` and `format` can
// still degrade to English month names on a tag Intl itself renders correctly. Report that, since
// the result is a page mixing localized and English dates.
export function getDateFnsLocale(tag: string = getLocaleTag()): Locale {
  const locale =
    DATE_FNS_LOCALES.find(candidate => candidate.code?.toLowerCase() === tag.toLowerCase()) ??
    DATE_FNS_LOCALES.find(candidate => languageOf(candidate.code ?? '') === languageOf(tag))

  if (!locale) {
    reportOnce(`No date-fns locale for "${tag}", date-fns patterns will use "${enUS.code}"`)
    return enUS
  }

  return locale
}

const formatters = new Map<string, Intl.DateTimeFormat>()

const formatterFor = (tag: string, style: DatePresentation) => {
  const key = `${tag}:${style}`
  let formatter = formatters.get(key)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(tag, PRESENTATION_FORMATS[style])
    formatters.set(key, formatter)
  }

  return formatter
}

const formatIn = (tag: string, style: DatePresentation, value: Date) => formatterFor(tag, style).format(value)

// U+2013 with a thin space either side, matching how Intl itself joins a range in en-US. Only the
// fallback below needs it; `formatRange` supplies the locale's own joiner.
const FALLBACK_RANGE_JOINER = '\u2009\u2013\u2009'

// `formatRange` is the whole point here, but it only reached Safari in 14.1, and `.browserslistrc`
// still admits Safari 14.0 and iOS 14.0-14.4. Calling it there is a TypeError that takes the view
// down, so fall back to joining the endpoints. That loses the locale's field collapsing -- es-US
// gets "19 mar - 31" rather than "19-31 de mar" -- which is the shape every browser renders today
// anyway, so the tail degrades to the status quo instead of crashing.
const formatRangeIn = (tag: string, style: DatePresentation, start: Date, end: Date) => {
  const formatter = formatterFor(tag, style)

  if (typeof formatter.formatRange !== 'function') {
    return `${formatter.format(start)}${FALLBACK_RANGE_JOINER}${formatter.format(end)}`
  }

  return formatter.formatRange(start, end)
}

interface ParsedDate {
  formattedDate: string
  isoDate: string
}

interface DateUtil {
  parseISODate: (input: string, format?: string) => ParsedDate
  format: (d: Date, formatStr: string) => string
  formatPresentation: (d: Date, style: DatePresentation) => string
  formatISOPresentation: (input: string, style: DatePresentation) => string
  formatPresentationRange: (start: Date, end: Date, style: DatePresentation) => string
  formatRelativePriorDate: (t: TFunction, date: Date, options?: { from?: Date; withTime?: boolean }) => string
  formatRelativeDistance: (date: Date | number, options?: { from?: Date }) => string
}

export const date = (localeTag: string = getLocaleTag()): DateUtil => {
  const tag = resolveTag(localeTag)
  const locale = getDateFnsLocale(tag)

  return {
    parseISODate(input, format) {
      const date = parseISO(input)
      return {
        formattedDate: isValid(date)
          ? dateFormat(date, format ?? 'MMM dd', {
              locale,
            })
          : '',
        isoDate: input,
      }
    },

    // Wrapper around date-fns format that automatically uses the resolved locale. Prefer
    // `formatPresentation` for anything a person reads: a pattern fixes the field order and the connector
    // words, which are locale data, so this stays correct only for patterns that are all digits.
    format(d: Date, formatStr: string): string {
      return dateFormat(d, formatStr, { locale })
    },

    // Prefer this over `format` for a plain date or time: a date-fns pattern hard-codes field
    // order, so `'MMM d'` renders "sept 22" in Spanish where the locale wants "22 sept".
    formatPresentation(d: Date, style: DatePresentation): string {
      return formatIn(tag, style, d)
    },

    // The presentation counterpart to `parseISODate`, for the common case of an ISO string that
    // goes straight to the screen. Parsing is left to date-fns `parseISO` rather than the `Date`
    // constructor on purpose: `new Date('2025-12-10')` is specified to read a date-only string as
    // UTC midnight, which is the previous day everywhere west of Greenwich, so "December 10"
    // renders as "December 9" for most of the US. `parseISO` reads it as local midnight.
    formatISOPresentation(input: string, style: DatePresentation): string {
      const parsed = parseISO(input)

      return isValid(parsed) ? formatIn(tag, style, parsed) : ''
    },

    // A date range is one phrase, not two dates with a dash interpolated between them. Which
    // fields collapse and where the shared field lands are locale data: en-US writes "Mar 19 – 31"
    // but es-US writes "19–31 de mar", moving the month to the end and attaching it to both
    // endpoints. Interpolating two independently formatted dates into `'{start}–{end}'` can only
    // ever produce the English shape, so let Intl build the whole phrase.
    // Returns '' rather than throwing on an unparseable endpoint, matching `formatISOPresentation`.
    // Intl raises a RangeError on an invalid Date, and a date that failed to parse upstream is a
    // missing title, not a reason to take the view down mid-render.
    formatPresentationRange(start: Date, end: Date, style: DatePresentation): string {
      if (!isValid(start) || !isValid(end)) {
        return ''
      }

      return formatRangeIn(tag, style, start, end)
    },

    formatRelativePriorDate(t, date, { from = new Date(), withTime = false } = {}) {
      const numberDaysAgo = differenceInCalendarDays(from, date)

      let dateString: string
      if (numberDaysAgo === 0) {
        dateString = t('Today', { context: 'label for the current day' })
      } else if (numberDaysAgo === 1) {
        dateString = t('Yesterday', { context: 'label for the prior day' })
      } else if (numberDaysAgo < 7 && !withTime) {
        dateString = formatIn(tag, 'weekday', date)
      } else {
        dateString = formatIn(tag, 'monthDay', date)
      }

      if (!withTime) {
        return dateString
      }

      return t('{date} at {time}', {
        date: dateString,
        time: formatIn(tag, 'time', date),
        context: 'a past date followed by a time of day, e.g. "Yesterday at 9:15 AM"',
      })
    },

    // Elapsed time in a single rounded unit, e.g. "2 hours ago" / "hace 2 horas".
    formatRelativeDistance(date, { from = new Date() } = {}) {
      return formatDistanceStrict(date, from, { addSuffix: true, locale })
    },
  }
}
